import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTaskStore } from '../store/taskStore'
import Spinner from '../components/common/Spinner'
import ImageCarousel from '../components/ImageCarousel'

export default function TaskDetailPage() {
  const { t } = useTranslation(['worker', 'common'])
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const {
    currentTask,
    myAssignments,
    submissions,
    fetchTaskDetail,
    fetchMyAssignments,
    loadSubmissionsForAssignment,
    claimTask,
    completeAssignment,
    isLoading,
  } = useTaskStore()

  useEffect(() => {
    if (taskId) fetchTaskDetail(taskId)
    fetchMyAssignments()
  }, [taskId, fetchTaskDetail, fetchMyAssignments])

  const assignment = myAssignments.find(
    (a) => a.task_id === taskId && ['claimed', 'in_progress', 'submitted'].includes(a.status)
  )
  useEffect(() => {
    if (assignment) loadSubmissionsForAssignment(assignment.id)
  }, [assignment?.id, loadSubmissionsForAssignment])

  const task = currentTask?.id === taskId ? currentTask : null
  const [claimError, setClaimError] = useState<string | null>(null)

  const sortedItems = useMemo(() => {
    return [...(task?.items ?? [])].sort((a, b) => a.sort_order - b.sort_order)
  }, [task])

  if (isLoading || !task) return <Spinner />

  const completedAssignment = myAssignments.find(
    (a) => a.task_id === taskId && ['completed', 'abandoned'].includes(a.status)
  )

  const allSubmitted = task.items.every((item) => submissions[item.id])
  const canComplete = assignment && allSubmitted

  const submittedCount = task.items.filter((item) => submissions[item.id]).length
  const progressFraction = task.items.length > 0 ? submittedCount / task.items.length : 0

  const totalReward =
    task.reward_per_item_coins != null ? task.reward_per_item_coins * task.items.length : null

  const handleClaim = async () => {
    setClaimError(null)
    try {
      await claimTask(task.id)
      await fetchMyAssignments()
    } catch (e: unknown) {
      setClaimError(e instanceof Error ? e.message : t('common:error.generic', { ns: 'common' }))
    }
  }

  const handleComplete = async () => {
    if (!assignment) return
    try {
      await completeAssignment(assignment.id)
      navigate('/')
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('common:error.generic', { ns: 'common' }))
    }
  }

  const showBottomBar = !!(claimError || completedAssignment || !assignment || canComplete)

  return (
    <div className={`min-h-screen safe-area-bottom ${showBottomBar ? 'pb-36' : ''}`} style={{ background: 'var(--app-page-bg)' }}>
      {/* ── Header ───────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 pt-4 pb-3"
        style={{ background: 'var(--app-card-bg)' }}
      >
        <button
          className="text-xl leading-none opacity-50"
          style={{ color: 'var(--tg-theme-text-color)' }}
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base leading-tight truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
            {task.title}
          </h1>
          {assignment && (
            <p className="text-xs opacity-50 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('taskDetail.ofFound', { done: submittedCount, total: task.items.length })}
            </p>
          )}
        </div>
        {totalReward != null && totalReward > 0 && (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 flex-shrink-0"
            style={{ borderColor: '#f59e0b' }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ background: '#f59e0b' }}
            >
              $
            </div>
            <span className="font-bold text-sm" style={{ color: '#f59e0b' }}>
              {totalReward.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* ── Thin progress bar ──────────────────────────────── */}
      {assignment && (
        <div className="w-full" style={{ height: 5, background: 'var(--app-page-bg)' }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${progressFraction * 100}%`,
              background: '#22c55e',
            }}
          />
        </div>
      )}

      {/* ── Task header image + description ──────────────────── */}
      {(() => {
        const images = [
          ...(task.header_image_url ? [task.header_image_url] : []),
          ...(task.gallery_images ?? []).map((img) => img.image_url),
        ]
        return images.length > 0 ? (
          <div className="w-full overflow-hidden" style={{ maxHeight: 220 }}>
            <ImageCarousel images={images} height={220} />
          </div>
        ) : null
      })()}

      {(task.description || task.target_location_name) && (
        <div className="px-4 pt-3">
          <div
            className="rounded-2xl px-4 py-3 space-y-1"
            style={{ background: 'var(--app-card-bg)' }}
          >
            {task.description && (
              <p className="text-sm leading-relaxed opacity-70" style={{ color: 'var(--tg-theme-text-color)' }}>
                {task.description}
              </p>
            )}
            {task.target_location_name && (
              <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-link-color)' }}>
                📍 {task.target_location_name}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Items mosaic grid ───────────────────────────────── */}
      <div className="px-4 pt-4 pb-4">
        <div className="grid grid-cols-3 gap-2">
          {sortedItems.map((item) => {
            const sub = submissions[item.id]
            const coverUrl = item.item_images?.[0]?.image_url ?? item.item_image_url
            const isDone = !!sub
            const isFound = sub?.submission_type === 'found'
            const isClickable = !!assignment

            return (
              <div
                key={item.id}
                className={`rounded-2xl overflow-hidden transition-opacity ${isClickable ? 'active:opacity-75 cursor-pointer' : 'cursor-default'}`}
                style={{
                  background: isDone && isFound
                    ? 'rgba(34,197,94,0.10)'
                    : isDone
                    ? 'rgba(239,68,68,0.08)'
                    : 'var(--app-card-bg)',
                }}
                onClick={() => {
                  if (isClickable) navigate(`/assignments/${assignment.id}/submit/${item.id}`)
                }}
              >
                {/* Image */}
                <div
                  className="relative w-full flex items-center justify-center"
                  style={{ aspectRatio: '1/1', background: isDone ? 'transparent' : 'var(--app-page-bg)' }}
                >
                  {coverUrl ? (
                    <img src={coverUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl opacity-60">
                      {isDone ? (isFound ? '✓' : '✗') : item.product_name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  {isDone && (
                    <div
                      className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ background: isFound ? '#22c55e' : '#ef4444' }}
                    >
                      {isFound ? '✓' : '✗'}
                    </div>
                  )}
                </div>

                {/* Name + status */}
                <div className="px-1.5 py-1.5">
                  <p
                    className="text-[11px] font-semibold leading-tight line-clamp-2"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {item.product_name}
                  </p>
                  {isDone && (
                    <p
                      className="text-[10px] font-semibold mt-0.5"
                      style={{ color: isFound ? '#22c55e' : '#ef4444' }}
                    >
                      {isFound
                        ? `✓ ${t('submission.found')}${sub.price_uzs != null ? ` · ${Number(sub.price_uzs).toLocaleString()}` : ''}`
                        : `✗ ${t('submission.notFound')}`}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Bottom action ──────────────────────────────────────── */}
      {showBottomBar && (
        <div
          className="fixed bottom-0 left-0 right-0 p-4 space-y-2 safe-area-bottom"
          style={{ background: 'var(--app-page-bg)' }}
        >
          {claimError && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ background: '#ef444418', color: '#ef4444' }}
            >
              {claimError}
            </div>
          )}
          {completedAssignment && !assignment ? (
            <div
              className="w-full py-3 rounded-2xl font-semibold text-base text-center"
              style={{ background: '#22c55e20', color: '#22c55e' }}
            >
              {completedAssignment.status === 'completed'
                ? t('taskDetail.alreadyCompleted')
                : t('taskDetail.alreadyAbandoned')}
            </div>
          ) : !assignment ? (
            <button
              className="w-full py-3 rounded-2xl font-semibold text-base"
              style={{
                background: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
              }}
              onClick={handleClaim}
            >
              {t('taskDetail.claimTask')}
            </button>
          ) : canComplete ? (
            <button
              className="w-full py-3 rounded-2xl font-semibold text-base"
              style={{ background: '#22c55e', color: 'white' }}
              onClick={handleComplete}
            >
              {t('taskDetail.completeSubmit')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}
